import { pdf } from '@react-pdf/renderer';
import QRCode from 'qrcode';
import { LabelLayout, PackageRow } from '@/services/api/packages';
import { LabelDocument } from './LabelDocument';
import { QR_LEVEL, labelKey, qrValue } from './labelConstants';
import dashLogo from '@/assets/dash-logo.png';

// Shared label PDF generation + print/download, used by both the single-item
// LabelPreviewModal and the bulk shipments print modal so there is one source
// of truth for how a label PDF is built and emitted.

// One QR per label (keyed by labelKey), encoding the item name so the logistic
// inbound scan can find the item by item_name. Falls back to deliveryId.
const buildQrMap = async (
  pkgs: PackageRow[]
): Promise<Record<string, string>> => {
  const entries = await Promise.all(
    pkgs.map(
      async (p) =>
        [
          labelKey(p),
          await QRCode.toDataURL(qrValue(p.itemName ?? p.deliveryId), {
            errorCorrectionLevel: QR_LEVEL,
            margin: 0,
            width: 216,
          }),
        ] as const
    )
  );
  return Object.fromEntries(entries);
};

// Render the labels to a real (vector) PDF entirely in the browser — no backend
// round-trip. Throws if react-pdf fails; callers surface the error.
export const buildLabelPdfBlob = async (
  pkgs: PackageRow[],
  layout: LabelLayout
): Promise<Blob> => {
  const qrMap = await buildQrMap(pkgs);
  return pdf(
    <LabelDocument
      packages={pkgs}
      layout={layout}
      qrMap={qrMap}
      logoUrl={dashLogo}
    />
  ).toBlob();
};

export const downloadLabelPdf = async (
  pkgs: PackageRow[],
  layout: LabelLayout,
  filename: string
): Promise<void> => {
  const blob = await buildLabelPdfBlob(pkgs, layout);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// Print the same PDF that download produces, via a hidden iframe, so printed
// output is identical to the file.
export const printLabelPdf = async (
  pkgs: PackageRow[],
  layout: LabelLayout
): Promise<void> => {
  const blob = await buildLabelPdfBlob(pkgs, layout);
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  iframe.src = url;
  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(url);
    }, 60000);
  };
  document.body.appendChild(iframe);
};
